import React from 'react';
import { Box } from 'reflexbox';
import WarningIcon from '@material-ui/icons/Warning';
import { getOrderPlaceDate } from '../../../../helpers';
import {
  getFulfilmentType,
  getOrderStatus,
  getWorkingHoursFormatted,
} from '../../helpers';
import DeclinedNote from '../DeclinedNote';
import FlaggedOrder from '../FlaggedOrder';
import SlaTimeslot from '../SlaTimeslot';
import LastSupportNote from '../LastSupportNote';
import TimeslotHeader from '../TimeslotHeader';
import ElipsisBox from '../../../Shared/ElipsisBox';
import Popover from '../../../Shared/Popover';
import ActionMenu from '../../../Shared/ActionMenu';
import { formatDateTimeHumanFriendly } from '../../../../utils/formatters';
import {
  DateWrapper,
  ElipsisBoxWrapper,
  EmptyNoteField,
} from '../LastSupportNote';
import moment from 'moment';
import { Loading } from '../../../../healtheraComponents';

const prepareData = ({
  orders,
  updatingOrders,
  query,
  handleAssign,
  handleReportIssue,
  handleAddNote,
  handleCancel,
  showNotesList,
}) => {
  if (!orders) {
    return [];
  }

  return orders.reduce(
    (summary, order) => {
      const {
        _id,
        patient_name,
        pharmacy_name,
        pharmacy_id,
        date_created,
        working_hours_since_creation,
        working_days_since_creation,
        working_hours_since_assigned,
        working_days_since_assigned,
        sla,
        rag_status,
        flagged,
        decline_reason: { reason } = {},
        last_note,
        delivery,
        repeat_id,
        otc = [],
        remedies = [],
        is_superdrug = false,
        issue_reported = false,
        private_prescription = null,
        support_notes = [],
        payment,
      } = order;

      const { text: note_text, date_created: note_date_created } = last_note || {};

      const { period: { start: slaStart, end: slaEnd } = {}, type: sla_type } = sla || {};

      const { timeslot: { start: deliveryStart, end: deliveryEnd } = {} } = delivery || {};

      let typeLabel = null;
      let itemsCount = 0;
      let firstItemName = null;
      let items = [];

      if (private_prescription) {
        typeLabel = 'Private';
        itemsCount = (otc?.length || 0) + (remedies?.length || 0);
        firstItemName = otc?.[0]?.product_name || remedies?.[0]?.medicine_name;
        items = otc;
      } else if (otc?.length) {
        typeLabel = remedies?.length ? 'OTC + Rx' : 'OTC';
        itemsCount = remedies?.length ? remedies.length + otc.length : otc.length;
        firstItemName = otc[0]?.product_name;
        items = [...otc, ...remedies];
      } else if (remedies?.length) {
        typeLabel = is_superdrug ? 'Referred Superdrug' : 'Referred';
        itemsCount = remedies.length;
        firstItemName = remedies[0]?.medicine_name;
        items = remedies;
      }

      const isUnassigned = !query?.status || query.status === 'unassigned';

      const shouldShowAssignAction = !!private_prescription;
      const shouldShowCancelAction =
        !!private_prescription && (isUnassigned || ['processing', 'ready'].includes(query.status));
      const isUpdating = updatingOrders?.some((id) => id === order._id);

      const menuOptions = [
        ...(shouldShowAssignAction
          ? [
              {
                key: 'assign_pharmacy',
                label: isUnassigned ? 'Assign pharmacy' : 'Re-assign pharmacy',
                action: () => handleAssign(order),
              },
            ]
          : []),
        {
          key: 'add_note',
          label: 'Add note',
          action: () => handleAddNote(order),
        },
        {
          key: 'report_issue',
          label: issue_reported ? 'Remove Issue' : 'Report an Issue',
          action: () => handleReportIssue(order),
          color: 'red',
        },
        ...(shouldShowCancelAction
          ? [
              {
                key: 'cancel_order',
                label: 'Cancel Order',
                action: () => handleCancel(order),
                color: 'red',
              },
            ]
          : []),
      ];

      const patientNameCellContent = [patient_name];
      const assignPharmacyAction = order.actions
        ?.reverse()
        .find((item) => ['assign_pharmacy', 'reassign_pharmacy'].includes(item.action));

      if (Number.isInteger(order.patient?.birthday)) {
        patientNameCellContent.push(<div>{moment(order.patient.birthday * 1000).format('MMMM DD, YYYY')}</div>);
      }

      summary.push({
        view: {
          patientName: <div>{patientNameCellContent}</div>,
          prescriptionId: order.private_prescription?.prescription_id || '',
          order: typeLabel ? (
            <div>
              <div>
                <b>{`${typeLabel} (${itemsCount})`}</b>
              </div>
              <Popover
                content={
                  <Box>
                    <ul style={{ padding: '10px 10px 10px 25px', margin: 0 }}>
                      {items.map((item) => (
                        <li style={{ padding: 0, margin: 0 }}>{item?.product_name || item?.medicine_name}</li>
                      ))}
                    </ul>
                  </Box>
                }
              >
                <ElipsisBox maxLine={'1'} text={firstItemName} />
              </Popover>
            </div>
          ) : null,
          type: getOrderStatus(order),
          orderPlaced: (
            <div>
              <div>{getOrderPlaceDate(date_created)}</div>
              <div>
                <b>{getWorkingHoursFormatted(working_hours_since_creation, working_days_since_creation)}</b>
              </div>
            </div>
          ),
          fulfilment: getFulfilmentType(order),
          timeslot: (
            <SlaTimeslot
              rag={rag_status}
              slaType={sla_type}
              slaTimeslot={{ slaStart, slaEnd }}
              deliveryTimeslot={{ deliveryStart, deliveryEnd }}
              orderData={{
                repeat_id,
                otc: otc?.[0],
                delivery: {
                  option: delivery?.option,
                  isPremium: Boolean(payment?.delivery_total),
                },
              }}
            />
          ),
          assignedAt: assignPharmacyAction ? (
            <div>
              <div>{getOrderPlaceDate(assignPharmacyAction.action_timestamp / 1000)}</div>
              <div>
                <b>{getWorkingHoursFormatted(working_hours_since_assigned, working_days_since_assigned)}</b>
              </div>
            </div>
          ) : (
            ''
          ),
          flagged: <FlaggedOrder flagged={flagged} />,
          declinedNote: <DeclinedNote note={reason} />,
          lastPharmacyNote: <LastSupportNote lastNote={note_text} date={note_date_created} pharmacyId={pharmacy_id} />,
          orderNote: (
            <>
              {support_notes?.length ? (
                <>
                  <ElipsisBoxWrapper
                    onClick={() => showNotesList(order)}
                    maxLine={1}
                    text={support_notes[support_notes.length - 1]?.text}
                  />
                  <DateWrapper>
                    {formatDateTimeHumanFriendly(support_notes[support_notes.length - 1]?.date_created * 1000)}
                  </DateWrapper>
                </>
              ) : (
                <EmptyNoteField />
              )}
            </>
          ),
          issue: issue_reported ? <WarningIcon color="error" /> : null,
          actions: isUpdating ? <Loading /> : <ActionMenu options={menuOptions} />,
          pharmacy_id: pharmacy_id || 'unassigned',
          pharmacy_name: pharmacy_name || 'Unassigned',
        },
        ID: _id,
        route: 'dashboard',
        skipId: true,
      });

      return summary;
    },
    [
      {
        view: {
          patientName: 'Patient name',
          ...(query?.type?.includes('private') ? { prescriptionId: {title: 'Prescription ID', width: '12%'} } : {}),
          order: 'Order',
          orderPlaced: { title: 'Order placed', width: '10%' },
          ...(query?.type?.includes('private') && query?.status !== 'unassigned' ? { assignedAt: {title: 'Assigned to Pharmacy', width: '8%'} } : {}),
          ...(query?.status !== 'unassigned' ? { fulfilment: 'Fulfilment' } : {}),
          timeslot: { title: <TimeslotHeader />, width: '13%' },
          flagged: 'Flag',
          orderNote: 'Notes',
          issue: { title: ' ', width: '3%' },
          actions: { title: ' ', width: '5%' },
        },
        headers: true,
      },
    ]
  );
};

export default prepareData;
